import React, { useEffect, useState } from 'react';
import { Newspaper, AlertCircle, ExternalLink } from 'lucide-react';
import { getPublishedBlogsByProfileId } from '../../../../services/firestoreService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * FeaturedBlogEditor - Editor for the featured-blog block
 * Fetches user's published blogs and lets them pick one to showcase
 */
const FeaturedBlogEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadBlogs = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const publishedBlogs = await getPublishedBlogsByProfileId(profileId);
        setBlogs(publishedBlogs);
      } catch (err) {
        console.error('Error loading blogs:', err);
        setError('Failed to load blogs');
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [profileId]);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  // Resolve content mode with backwards compat: legacy blocks without contentMode
  // are treated as 'specific' when they have a blogId, else 'most-recent'.
  const contentMode = content.contentMode || (content.blogId ? 'specific' : 'most-recent');

  // blogs[] is already sorted newest-first by getPublishedBlogsByProfileId.
  const mostRecentBlog = blogs[0] || null;
  const selectedBlog =
    contentMode === 'most-recent' ? mostRecentBlog : blogs.find((b) => b.id === content.blogId);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* Content Mode Toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Show
        </label>
        <div className="flex gap-2">
          {[
            { value: 'most-recent', label: 'Most Recent' },
            { value: 'specific', label: 'Specific Post' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('contentMode', option.value)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                contentMode === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {contentMode === 'most-recent'
            ? 'Auto-updates to your newest published post.'
            : 'Always shows the post you pick below.'}
        </p>
      </div>

      {/* Blog Selector — only when mode is 'specific' */}
      {contentMode === 'specific' && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Select Blog Post
          </label>
          {loading ? (
            <div className="flex items-center gap-2 py-3 text-gray-400 text-sm">
              <div className="w-4 h-4 border-2 border-gray-600 border-t-[#749555] rounded-full animate-spin" />
              Loading blogs...
            </div>
          ) : error ? (
            <p className="text-red-400 text-sm flex items-center gap-1">
              <AlertCircle size={14} />
              {error}
            </p>
          ) : blogs.length === 0 ? (
            <div className="text-center py-6 bg-[#1a1a2e] border border-gray-700 rounded-lg">
              <Newspaper size={28} className="mx-auto text-gray-600 mb-2" />
              <p className="text-gray-500 text-sm">No published blog posts</p>
              <p className="text-gray-600 text-xs">Publish a blog post first to use this block</p>
            </div>
          ) : (
            <select
              value={content.blogId || ''}
              onChange={(e) => handleChange('blogId', e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white focus:border-[#749555] focus:outline-none"
            >
              <option value="">Choose a blog post...</option>
              {blogs.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.title} — {formatDate(b.publishedAt || b.createdAt)}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Selected/Most-Recent Blog Preview */}
      {selectedBlog && (
        <div className="flex items-center gap-3 p-3 bg-[#749555]/10 border border-[#749555]/30 rounded-lg">
          {selectedBlog.imageUrl ? (
            <img
              src={selectedBlog.imageUrl}
              alt={selectedBlog.title}
              className="w-14 h-14 object-cover rounded flex-shrink-0"
            />
          ) : (
            <div className="w-14 h-14 bg-gray-800 rounded flex items-center justify-center flex-shrink-0">
              <Newspaper size={20} className="text-gray-500" />
            </div>
          )}
          <div className="min-w-0">
            {contentMode === 'most-recent' && (
              <p className="text-[10px] uppercase tracking-wide text-[#749555] font-semibold mb-0.5">
                Currently showing
              </p>
            )}
            <p className="text-sm text-white font-medium truncate">{selectedBlog.title}</p>
            <p className="text-xs text-[#749555]">{formatDate(selectedBlog.publishedAt || selectedBlog.createdAt)}</p>
          </div>
        </div>
      )}

      {/* Empty state for most-recent mode when no blogs exist */}
      {contentMode === 'most-recent' && !loading && blogs.length === 0 && (
        <div className="text-center py-6 bg-[#1a1a2e] border border-gray-700 rounded-lg">
          <Newspaper size={28} className="mx-auto text-gray-600 mb-2" />
          <p className="text-gray-500 text-sm">No published blog posts yet</p>
          <p className="text-gray-600 text-xs">This block will auto-fill once you publish a post.</p>
        </div>
      )}

      {/* Button Text */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Button Text
        </label>
        <input
          type="text"
          value={content.buttonText || 'Read More'}
          onChange={(e) => handleChange('buttonText', e.target.value)}
          placeholder="Read More"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Layout */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Layout
        </label>
        <div className="flex gap-2">
          {[
            { value: 'imageLeft', label: 'Image Left' },
            { value: 'imageRight', label: 'Image Right' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('layout', option.value)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                content.layout === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Show Excerpt */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={content.showExcerpt !== false}
          onChange={(e) => handleChange('showExcerpt', e.target.checked)}
          className="w-4 h-4 rounded border-gray-600 bg-[#1a1a2e] text-[#749555] focus:ring-[#749555]"
        />
        <span className="text-sm text-gray-300">Show Excerpt</span>
      </label>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['featured-blog']}
      />
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['featured-blog']}
        theme={theme || {}}
        blockType="featured-blog"
      />
      </>)}
      <a
        href="/dashboard/website/blogs"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
      >
        Manage Blogs
        <ExternalLink size={14} />
      </a>
    </div>
  );
};

export default FeaturedBlogEditor;
